/**
 * The ONLY place createTheme() is called.
 * Assembles all foundation modules and component overrides.
 *
 * Imports directly from foundation files, never from index.ts,
 * to avoid accidentally consuming @deprecated re-exports.
 *
 *  FIGMA reference:
 *  https://www.figma.com/design/CqO24eewdxuBg3MkLbj2QX/BHB-components?node-id=6112-51415&t=cqV7qec26xmWuSV7-0
 */
import { grey } from "@mui/material/colors";
import { createTheme } from "@mui/material/styles";

// Component overrides
import { dialogOverrides } from "./components/dialog";
import { toolbarOverrides } from "./components/toolbar";
// Breakpoints
import { buildBreakpoints } from "./foundations/breakpoints";
// Foundations
import {
  colorTokens,
  LegacyPrimary,
  LegacySecondary,
  textTokens,
  uiTokens
} from "./foundations/colors";
import { layoutTokens } from "./foundations/layout";
import {
  FontSize,
  FontWeight,
  typographyVariants
} from "./foundations/typography";

export const createAppTheme = () => {
  const theme = createTheme({
    palette: {
      primary: {
        // Legacy numeric shades — kept so existing palette.primary[800]
        // call-sites don't break. Remove once migration is complete.
        ...LegacyPrimary,
        main: colorTokens.primary,
        light: colorTokens.primary50,
        dark: "#0D2C45"
      },
      secondary: {
        ...LegacySecondary,
        main: colorTokens.secondary
      },
      error: {
        main: colorTokens.error
      },
      text: {
        // Kept as original rgba strings to not affect existing components.
        // New components should use textTokens.dark.* directly.
        primary: "rgba(0, 0, 0, .87)",
        secondary: "rgba(0, 0, 0, .54)",
        disabled: "rgba(0, 0, 0, .38)"
      },
      background: {
        default: grey[100]
      },

      header: {
        // v2 — new design tokens
        newMain: colorTokens.primary,
        newSub: textTokens.dark.primary,
        newByline: textTokens.dark.primary,
        // v1 — TODO: remove after new header design is approved and implemented
        main: colorTokens.lightest,
        sub: grey[50],
        byline: "#5c5c5c"
      },

      separator: grey[200],

      accent: {
        iconBackground: LegacyPrimary[800],
        containerBackground: LegacyPrimary[800],
        // v2 — TODO: rename and remove containerBackground once migrated
        containerBackgroundNew: colorTokens.brightBlue,
        text: textTokens.dark.primary,
        disabled: colorTokens.dark38,
        bookingSuccess: LegacyPrimary[50]
      },

      button: {
        borderColor: colorTokens.transparencyDarkAt23,
        textColor: textTokens.dark.secondary
      },

      // v2 — Figma "Tokens" collection (1-to-1 mapping)
      tokens: uiTokens,

      // v2 — Figma "Text" collection
      textTokens
    },

    breakpoints: buildBreakpoints(),

    typography: {
      // Original values preserved exactly
      headline: {
        fontSize: "24px",
        fontWeight: FontWeight.regular,
        lineHeight: "1.167",
        letterSpacing: "-0.01562em"
      },
      title1: {
        fontSize: "20px",
        fontWeight: FontWeight.medium,
        lineHeight: "1.2",
        letterSpacing: "-0.00833em"
      },
      title2: {
        fontSize: "20px",
        fontWeight: FontWeight.regular,
        lineHeight: "1.167",
        letterSpacing: "0em"
      },
      body1: {
        fontSize: "14px",
        fontWeight: FontWeight.regular,
        lineHeight: "1.5",
        letterSpacing: "0.00938em"
      },
      body2: {
        fontSize: "13px",
        fontWeight: FontWeight.medium,
        lineHeight: "1.5",
        letterSpacing: "0.00938em"
      },
      body3: {
        fontSize: "16px",
        fontWeight: FontWeight.regular,
        lineHeight: "1.5",
        letterSpacing: "0.00938em"
      },
      body4: {
        fontSize: "14px",
        fontWeight: FontWeight.medium,
        lineHeight: "1.5",
        letterSpacing: "0.00938em"
      },
      button: {
        fontSize: "14px",
        fontWeight: FontWeight.medium,
        textTransform: "none"
      },
      // v2 additions from foundations/typography.ts
      ...typographyVariants
    },

    // v2 — layout tokens available via useTheme().layout
    layout: layoutTokens,

    components: {
      ...dialogOverrides,
      ...toolbarOverrides,
      MuiTypography: {
        defaultProps: {
          variantMapping: {
            // Todo: Remove 'n' prefixed once old variants removed
            nh1: "h1",
            nh2: "h2",
            nh3: "h3",
            nh4: "h4",
            nbody1: "p",
            nbody2: "p",
            caption: "p",
            // Mirrors mapping done by removed Literal component, can be removed once use of old variants cleaned up
            headline: "h1",
            title1: "h2",
            title2: "h3",
            body1: "span",
            body2: "span",
            body3: "span",
            body4: "span"
          }
        }
      }
    }
  });

  theme.typography.nbody1[theme.breakpoints.down("md")] = {
    fontSize: FontSize.lg,
    letterSpacing: "0.4px"
  };

  return theme;
};
